import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USE_REAL_API } from '@/services/api-client';
import * as reqApi from '@/services/requests-api';

export type RequestType = 'leave' | 'expense' | 'overtime' | 'travel' | 'makeup_clock';
export type RequestState = 'pending' | 'approved' | 'rejected';

export interface MyRequestItem {
  id: string;
  type: RequestType;
  title: string;
  submittedAt: string;
  state: RequestState;
}

export interface CreateRequestInput {
  type: RequestType;
  title: string;
  description: string;
  /** Free-form per-type extra fields */
  extra?: Record<string, string | number>;
}

const MOCK: MyRequestItem[] = [
  {
    id: 'R-100',
    type: 'leave',
    title: 'Annual Leave — 2 days · Apr 18–19',
    submittedAt: '2 days ago',
    state: 'approved',
  },
  {
    id: 'R-099',
    type: 'expense',
    title: 'Client dinner · ¥420',
    submittedAt: '4 days ago',
    state: 'approved',
  },
];

let mockSeq = 200;

export function useMyRequests() {
  return useQuery({
    queryKey: ['my-requests', USE_REAL_API],
    queryFn: async (): Promise<MyRequestItem[]> => {
      if (USE_REAL_API) return reqApi.fetchMyRequests();
      await new Promise((r) => setTimeout(r, 60));
      return [...MOCK].sort((a, b) => Number(b.id.slice(2)) - Number(a.id.slice(2)));
    },
    staleTime: 30_000,
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRequestInput): Promise<MyRequestItem> => {
      if (USE_REAL_API) {
        const amount = input.extra && typeof input.extra['amount'] === 'string'
          ? Number(input.extra['amount'])
          : undefined;
        return reqApi.submitRequest({
          type: input.type,
          title: input.title,
          description: input.description,
          ...(amount !== undefined && Number.isFinite(amount) ? { amount } : {}),
        });
      }
      await new Promise((r) => setTimeout(r, 350));
      const item: MyRequestItem = {
        id: `R-${mockSeq++}`,
        type: input.type,
        title: input.title,
        submittedAt: 'just now',
        state: 'pending',
      };
      MOCK.unshift(item);
      return item;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-requests'] });
    },
  });
}

export const REQUEST_TYPES: Array<{ key: RequestType; icon: string; labelKey: string }> = [
  { key: 'leave', icon: 'event_busy', labelKey: 'requests.type_leave' },
  { key: 'expense', icon: 'receipt_long', labelKey: 'requests.type_expense' },
  { key: 'overtime', icon: 'more_time', labelKey: 'requests.type_overtime' },
  { key: 'travel', icon: 'flight_takeoff', labelKey: 'requests.type_travel' },
  { key: 'makeup_clock', icon: 'schedule', labelKey: 'requests.type_makeup' },
];
